/**
 * Registration desk cards: one per person found by CI.
 *  - DeskMinisterCard: active minister not registered yet → "Inscribir" (walk-in)
 *  - DeskParticipantCard: registered (pre-registered online or at the desk) → steps + next action
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, FileText, KeyRound, Unlock, Tag, Loader2, Printer, Receipt as ReceiptIcon, UserPlus, Vote } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { formatPrice } from '@/models/event';
import { getParticipantPayments, type EventPaymentMethod } from '@/Services/eventPayments.service';
import { PaymentMethodToggle, type DeskPaymentInfo } from './PaymentMethodToggle';
import { getRegistrationDocumentUrl, replaceRegistrationDocument } from '../../public/publicRegistrationRepository';
import { DocumentUploadField } from '../../public/components/DocumentUploadField';
import { DeskMinisterDialog, type DeskEventInfo } from './DeskMinisterDialog';
import { formatReceiptNumber } from '@/features/receipts/types';
import { setVotingEnabled } from '@/Services/eventParticipants.service';
import { regeneratePin, unlockCiForEvent } from '@/features/voting/data/votingRepository';
import { useEventHasVoting } from '../../hooks/useEventHasVoting';
import { chargeAndIssueReceipt, issueReceiptForPayment, reviewRegistration } from '../deskRepository';
import {
  getDeskSteps,
  getNextAction,
  isFree,
  outstandingAmount,
  paymentsMissingReceipt,
  voucherRequired,
  type DeskContact,
  type DeskKind,
  type DeskPayment,
  type DeskPerson,
  type DeskPricing,
  type DeskParticipant,
} from '../deskLogic';

const KIND_LABEL: Record<DeskKind, string> = { MINISTER: 'Ministro', DELEGATE: 'Delegado', GUEST: 'Invitado' };

const DOC_LABELS: Record<string, string> = {
  ci_front: 'CI anverso',
  ci_back: 'CI reverso',
  voucher: 'Comprobante de pago',
  delegation_letter: 'Carta de delegación',
  signature: 'Firma',
};

interface CommonProps {
  eventId: string;
  eventName: string;
  paymentInfo: DeskPaymentInfo;
  eventOpen: boolean;
  canCharge: boolean;
  onChanged: () => void;
  onPrintReceipt: (receiptId: string) => void;
  /** Hands the credential over: marks attendance and opens the sticker */
  onDeliverCredential: (person: DeskPerson, kind: DeskKind, participant: DeskParticipant) => void;
  /** Registration finished: clear the screen for the next person */
  onNewParticipant: () => void;
}

function PersonHeader({ person, kind }: { person: DeskPerson; kind: DeskKind }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-lg font-semibold">{person.full_name}</p>
        <p className="text-sm text-muted-foreground">
          {[person.dni && `CI ${person.dni}`, person.adb_code, person.district_name, person.region_name]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      <Badge variant={kind === 'MINISTER' ? 'secondary' : 'default'}>{KIND_LABEL[kind]}</Badge>
    </div>
  );
}

// ─── Walk-in minister ───────────────────────────────────────────────────────

export function DeskMinisterCard({
  person,
  pricing,
  contact,
  event,
  eventId,
  eventOpen,
  onChanged,
}: Pick<CommonProps, 'eventId' | 'eventOpen' | 'onChanged'> & {
  person: DeskPerson;
  pricing: DeskPricing;
  contact: DeskContact;
  event: DeskEventInfo;
}) {
  const [formOpen, setFormOpen] = useState(false);

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <PersonHeader person={person} kind="MINISTER" />
        <p className="text-sm text-muted-foreground">No está inscrito en este evento.</p>
        {!eventOpen && (
          <p className="flex items-center gap-1.5 text-sm text-amber-700">
            <AlertTriangle className="size-4" /> El evento no está abierto a inscripciones.
          </p>
        )}
        <Button size="lg" onClick={() => setFormOpen(true)} disabled={!eventOpen}>
          <UserPlus className="mr-2 size-4" />
          Inscribir en el evento
        </Button>
      </CardContent>
      {formOpen && (
        <DeskMinisterDialog
          eventId={eventId}
          event={event}
          person={person}
          pricing={pricing}
          contact={contact}
          open={formOpen}
          onOpenChange={setFormOpen}
          onRegistered={onChanged}
        />
      )}
    </Card>
  );
}

/** Bank voucher photo, needed only when paying by transfer / QR (cash has no voucher) */
function VoucherSection({
  eventId,
  method,
  existingKey,
  value,
  onChange,
  disabled,
}: {
  eventId: string;
  method: EventPaymentMethod;
  existingKey?: string;
  value: string | undefined;
  onChange: (key: string | undefined) => void;
  disabled?: boolean;
}) {
  if (method !== 'bank_transfer') return null;
  if (existingKey) return <p className="text-xs text-muted-foreground">El comprobante ya está adjunto a la inscripción.</p>;
  return (
    <DocumentUploadField
      eventId={eventId}
      kind="voucher"
      label="Comprobante de la transferencia / QR"
      hint="Foto de la confirmación del banco; queda como respaldo del pago."
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

// ─── Registered person ──────────────────────────────────────────────────────

export function DeskParticipantCard({
  person,
  kind,
  participant,
  eventId,
  eventName,
  paymentInfo,
  canCharge,
  onChanged,
  onPrintReceipt,
  onDeliverCredential,
  onNewParticipant,
}: CommonProps & { person: DeskPerson; kind: DeskKind; participant: DeskParticipant }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState<EventPaymentMethod>('cash');
  const [amount, setAmount] = useState<string>('');
  const [alsoCharge, setAlsoCharge] = useState(true);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [openingDoc, setOpeningDoc] = useState<string | null>(null);
  const [voucherKey, setVoucherKey] = useState<string | undefined>();
  const [votingBusy, setVotingBusy] = useState(false);
  const hasVoting = useEventHasVoting(eventId);
  const [pinBusy, setPinBusy] = useState(false);

  // Habilitado para votar: ministers and delegates vote by default, plain guests do not
  const votingOn = participant.voting_enabled ?? (kind === 'MINISTER' || kind === 'DELEGATE' || !!participant.is_delegate);
  const unlock = async () => {
    setPinBusy(true);
    try {
      await unlockCiForEvent(eventId, person.dni ?? '');
      toast({ title: 'CI desbloqueado', description: 'La persona puede volver a intentar con su PIN.' });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo desbloquear', variant: 'destructive' });
    } finally {
      setPinBusy(false);
    }
  };
  const newPin = async () => {
    setPinBusy(true);
    try {
      await regeneratePin({ participantId: participant.id });
      onChanged();
      toast({ title: 'PIN nuevo generado', description: 'Imprima de nuevo el sticker para que lo lleve la persona.' });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo generar el PIN', variant: 'destructive' });
    } finally {
      setPinBusy(false);
    }
  };
  const toggleVoting = async (enabled: boolean) => {
    setVotingBusy(true);
    try {
      await setVotingEnabled([participant.id], enabled);
      onChanged();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo cambiar', variant: 'destructive' });
    } finally {
      setVotingBusy(false);
    }
  };

  const { data: rawPayments = [], refetch: refetchPayments } = useQuery({
    queryKey: ['desk_payments', participant.id],
    queryFn: () => getParticipantPayments(participant.id),
  });
  const payments: DeskPayment[] = rawPayments.map((p) => ({
    id: p.id,
    amount: Number(p.amount),
    status: p.status,
    receipt_id: p.receipt_id,
  }));

  const steps = getDeskSteps(participant, payments);
  const next = getNextAction(participant, payments);
  const owed = outstandingAmount(participant);
  const chargeAmount = amount === '' ? owed : Number(amount);
  const missing = paymentsMissingReceipt(payments);
  const receipts = rawPayments.filter((p) => p.status === 'RECORDED' && p.receipt);

  const refresh = async () => {
    await refetchPayments();
    queryClient.invalidateQueries({ queryKey: ['desk_lookup', eventId] });
    onChanged();
  };

  const run = async (fn: () => Promise<void>, errorTitle: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : errorTitle, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const existingVoucher = participant.documents?.voucher;
  const voucherMissing = voucherRequired(method, !!(existingVoucher || voucherKey));

  /** A voucher uploaded at the desk also becomes part of the registration's documents */
  const attachVoucher = async () => {
    if (!voucherKey || existingVoucher) return;
    try {
      await replaceRegistrationDocument(participant.id, 'voucher', voucherKey);
    } catch (err) {
      toast({
        title: 'Pago registrado, pero no se pudo adjuntar el comprobante a la inscripción',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    }
  };

  const chargeInput = (value: number) => ({
    eventName,
    participantId: participant.id,
    payerName: person.full_name ?? '',
    payerCi: person.dni,
    ministerId: person.minister_id,
    amount: value,
    method,
    voucherKey: method === 'bank_transfer' ? (voucherKey ?? existingVoucher ?? null) : null,
  });

  const charge = (value: number) =>
    run(async () => {
      const result = await chargeAndIssueReceipt(chargeInput(value));
      await attachVoucher();
      if (result.receipt) {
        toast({ title: `Pago registrado · recibo ${formatReceiptNumber(result.receipt.number)}` });
        onPrintReceipt(result.receipt.id);
      } else {
        toast({
          title: 'Pago registrado, pero el recibo no se emitió',
          description: result.receiptError,
          variant: 'destructive',
        });
      }
      await refresh();
    }, 'No se pudo registrar el pago');

  const verify = () =>
    run(async () => {
      await reviewRegistration(participant.id, 'VERIFY');
      toast({ title: 'Inscripción verificada' });
      if (alsoCharge && owed > 0 && canCharge) {
        const result = await chargeAndIssueReceipt(chargeInput(owed));
        await attachVoucher();
        if (result.receipt) onPrintReceipt(result.receipt.id);
        else toast({ title: 'Pago registrado, pero el recibo no se emitió', description: result.receiptError, variant: 'destructive' });
      }
      await refresh();
    }, 'No se pudo verificar');

  const reject = () =>
    run(async () => {
      await reviewRegistration(participant.id, 'REJECT', rejectReason.trim());
      toast({ title: 'Inscripción rechazada' });
      setShowReject(false);
      await refresh();
    }, 'No se pudo rechazar');

  const issueMissing = () =>
    run(async () => {
      const payment = rawPayments.find((p) => p.id === missing[0].id);
      if (!payment) return;
      const receipt = await issueReceiptForPayment({
        ...chargeInput(Number(payment.amount)),
        paymentId: payment.id,
        method: payment.method ?? method,
      });
      toast({ title: `Recibo ${formatReceiptNumber(receipt.number)} emitido` });
      onPrintReceipt(receipt.id);
      await refresh();
    }, 'No se pudo emitir el recibo');

  const openDocument = async (key: string) => {
    setOpeningDoc(key);
    const tab = window.open('', '_blank');
    try {
      const url = await getRegistrationDocumentUrl(key);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
    } catch (err) {
      tab?.close();
      toast({ title: err instanceof Error ? err.message : 'No se pudo abrir', variant: 'destructive' });
    } finally {
      setOpeningDoc(null);
    }
  };

  const documents = Object.entries(participant.documents ?? {}).filter(([, key]) => !!key);
  const verified = participant.registration_status === 'VERIFIED';

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <PersonHeader person={person} kind={kind} />

        <ol className="flex flex-wrap gap-2" aria-label="Avance del registro">
          {steps.map((s) => (
            <li
              key={s.id}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${
                s.state === 'done'
                  ? 'border-green-300 bg-green-50 text-green-800'
                  : s.state === 'blocked'
                    ? 'border-red-300 bg-red-50 text-red-800'
                    : 'border-amber-300 bg-amber-50 text-amber-800'
              }`}
            >
              {s.state === 'done' ? <Check className="size-3.5" /> : <AlertTriangle className="size-3.5" />}
              {s.label}
            </li>
          ))}
        </ol>

        <p className="text-sm text-muted-foreground">
          {isFree(participant) ? 'Inscripción sin costo' : <>Monto {formatPrice(Number(participant.calculated_price ?? 0))} · Pagado {formatPrice(Number(participant.payment_amount ?? 0))}</>}
          {participant.registration_source === 'PUBLIC' && ' · Preinscripción en línea'}
        </p>

        {next === 'REJECTED' && (
          <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            Inscripción rechazada{participant.rejection_reason ? `: ${participant.rejection_reason}` : ''}. Consulte con la
            administración.
          </p>
        )}

        {next === 'REVIEW' && (
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Verificar la preinscripción</p>
            {documents.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {documents.map(([docKind, key]) => (
                  <Button key={docKind} variant="outline" size="sm" onClick={() => openDocument(key as string)} disabled={openingDoc === key}>
                    {openingDoc === key ? <Loader2 className="mr-2 size-4 animate-spin" /> : <FileText className="mr-2 size-4" />}
                    {DOC_LABELS[docKind] ?? docKind}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin documentos adjuntos.</p>
            )}

            {!showReject && owed > 0 && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={alsoCharge} onCheckedChange={(c) => setAlsoCharge(c === true)} disabled={!canCharge} />
                  Registrar el pago de {formatPrice(owed)} y emitir recibo
                </label>
                {alsoCharge && <PaymentMethodToggle value={method} onChange={setMethod} paymentInfo={paymentInfo} disabled={busy} />}
                {alsoCharge && (
                  <VoucherSection
                    eventId={eventId}
                    method={method}
                    existingKey={existingVoucher}
                    value={voucherKey}
                    onChange={setVoucherKey}
                    disabled={busy}
                  />
                )}
              </div>
            )}

            {showReject ? (
              <div className="space-y-2">
                <Label htmlFor="desk-reject">Motivo del rechazo</Label>
                <Textarea id="desk-reject" rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowReject(false)} disabled={busy}>Cancelar</Button>
                  <Button variant="destructive" onClick={reject} disabled={busy || !rejectReason.trim()}>Confirmar rechazo</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button size="lg" onClick={verify} disabled={busy || (alsoCharge && owed > 0 && canCharge && voucherMissing)}>
                  {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
                  Verificar{alsoCharge && owed > 0 && canCharge ? ' y cobrar' : ''}
                </Button>
                <Button variant="outline" onClick={() => setShowReject(true)} disabled={busy}>Rechazar</Button>
              </div>
            )}
          </div>
        )}

        {next === 'CHARGE' && (
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Cobrar la inscripción</p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="desk-amount" className="text-xs">Monto (Bs)</Label>
                <Input
                  id="desk-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-32"
                  value={amount === '' ? owed.toFixed(2) : amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>
            <PaymentMethodToggle value={method} onChange={setMethod} paymentInfo={paymentInfo} disabled={busy} />
            <VoucherSection
              eventId={eventId}
              method={method}
              existingKey={existingVoucher}
              value={voucherKey}
              onChange={setVoucherKey}
              disabled={busy}
            />
            {chargeAmount > 0 && chargeAmount < owed && (
              <p className="text-xs text-amber-700">Pago parcial: quedará un saldo de {formatPrice(owed - chargeAmount)}.</p>
            )}
            {chargeAmount > owed && <p className="text-xs text-destructive">El monto supera el saldo ({formatPrice(owed)}).</p>}
            <Button
              size="lg"
              onClick={() => charge(chargeAmount)}
              disabled={busy || !canCharge || !(chargeAmount > 0) || chargeAmount > owed || voucherMissing}
              title={canCharge ? undefined : 'No tiene permiso para registrar pagos'}
            >
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <ReceiptIcon className="mr-2 size-4" />}
              Cobrar e imprimir recibo
            </Button>
          </div>
        )}

        {next === 'ISSUE_RECEIPT' && (
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">Pago registrado sin recibo</p>
            <Button size="lg" onClick={issueMissing} disabled={busy || !canCharge}>
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <ReceiptIcon className="mr-2 size-4" />}
              Emitir e imprimir recibo
            </Button>
          </div>
        )}

        {next === 'CREDENTIAL' && (
          <p className="flex items-center gap-1.5 text-sm font-medium text-green-700">
            <Check className="size-4" /> Listo: entregue la credencial.
          </p>
        )}

        {next === 'DONE' && (
          <p className="flex items-center gap-1.5 text-sm font-medium text-green-700">
            <Check className="size-4" /> Credencial entregada
            {participant.checked_in_at &&
              ` · asistencia registrada ${new Date(participant.checked_in_at).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}`}
          </p>
        )}

        {hasVoting && participant.registration_status !== 'REJECTED' && (
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <Label htmlFor={`voting-${participant.id}`} className="flex items-center gap-2 text-sm font-medium">
              <Vote className="size-4 text-muted-foreground" /> Habilitado para votar
            </Label>
            <Switch
              id={`voting-${participant.id}`}
              checked={votingOn}
              disabled={votingBusy}
              onCheckedChange={(v) => void toggleVoting(v)}
            />
            {votingOn && participant.voting_pin && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                PIN <span className="font-mono text-base font-semibold text-foreground">{participant.voting_pin}</span>
                <Button size="icon" variant="ghost" className="size-7" title="Generar un PIN nuevo (credencial perdida)" disabled={pinBusy} onClick={() => void newPin()}>
                  <KeyRound className="size-4" />
                </Button>
                <Button size="icon" variant="ghost" className="size-7" title="Desbloquear el CI (varios PIN incorrectos)" disabled={pinBusy || !person.dni} onClick={() => void unlock()}>
                  <Unlock className="size-4" />
                </Button>
              </span>
            )}
          </div>
        )}

        {verified && (
          <div className="flex flex-wrap gap-2 border-t pt-3">
            <Button
              size="lg"
              variant={next === 'CREDENTIAL' ? 'default' : 'outline'}
              onClick={() => onDeliverCredential(person, kind, participant)}
            >
              <Tag className="mr-2 size-4" />
              {participant.attendance_status === 'ATTENDED' ? 'Reimprimir sticker con QR' : 'Entregar credencial e imprimir sticker'}
            </Button>
            {receipts.map((p) => (
              <Button key={p.id} variant="outline" onClick={() => onPrintReceipt(p.receipt!.id)}>
                <Printer className="mr-2 size-4" /> Recibo N° {formatReceiptNumber(p.receipt!.number)}
              </Button>
            ))}
          </div>
        )}

        {(next === 'CREDENTIAL' || next === 'DONE') && (
          <Button size="lg" variant="secondary" className="h-14 w-full border text-base" onClick={onNewParticipant}>
            <UserPlus className="mr-2 size-5" /> Registrar nuevo participante
          </Button>
        )}
      </CardContent>
    </Card>
  );
}


